import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Form, Button, Card } from 'react-bootstrap';
import { Link, useSearchParams, useLocation } from 'react-router-dom';
import { getWriters } from "../../api";
import './SearchResults.css';

const SearchResults = () => {
    const [searchParams] = useSearchParams();
    const location = useLocation();
    const [sortBy, setSortBy] = useState('relevance');
    const [results, setResults] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [writers, setWriters] = useState([]);
    const [selectedFilters, setSelectedFilters] = useState({
        years: [],
        authors: [],
        topics: []
    });

    // URL'den arama parametrelerini al
    const isAdvancedSearch = searchParams.get('advanced') === 'true';
    const searchQuery = searchParams.get('q');
    
    // Gelişmiş arama parametreleri
    const advancedParams = {
        author: searchParams.get('author') || searchParams.get('selectedAuthor'),
        title: searchParams.get('title'),
        content: searchParams.get('content'),
        timeRange: searchParams.get('timeRange'),
        school: searchParams.get('school'),
        type: searchParams.get('type'),
        topic: searchParams.get('topic') || searchParams.get('selectedTopic'),
        keywords: searchParams.get('keywords')
    };

    useEffect(() => {
        console.log('🔍 SearchResults component yüklendi');
        console.log('📍 Location state:', location.state);
        console.log('🔗 Search params:', Object.fromEntries(searchParams));
        
        // Writers'ı yükle
        const fetchWriters = async () => {
            try {
                const response = await getWriters();
                if (response && response.data) {
                    setWriters(response.data);
                    console.log('✅ Writers yüklendi:', response.data);
                }
            } catch (error) {
                console.error('❌ Writers yüklenirken hata:', error);
            }
        };
        
        fetchWriters();
        
        // Location state'den sonuçları al
        if (location.state && location.state.searchResults) {
            console.log('✅ Advanced search sonuçları location state\'den alındı:', location.state.searchResults);
            setResults(location.state.searchResults);
            setLoading(false);
        } else {
            console.log('⚠️ Location state\'de searchResults bulunamadı, normal arama yapılacak');
            // Normal arama logic'i burada olacak
            setResults([]);
            setLoading(false);
        }
    }, [location.state, searchParams]);

  

    const handleFilterChange = (category, value) => {
        setSelectedFilters(prev => ({
            ...prev,
            [category]: prev[category].includes(value)
                ? prev[category].filter(item => item !== value)
                : [...prev[category], value]
        }));
    };

    // Arama başlığını oluştur
    const getSearchTitle = () => {
        if (isAdvancedSearch) {
            // Yazar ismini ID'den bul
            const getAuthorName = (authorId) => {
                const writer = writers.find(w => w.id.toString() === authorId.toString());
                return writer ? `${writer.name} ${writer.surname}` : `Yazar ID: ${authorId}`;
            };
            
            const activeFilters = Object.entries(advancedParams)
                .filter(([_, value]) => value && value.trim() !== '')
                .map(([key, value]) => {
                    switch(key) {
                        case 'selectedAuthor':
                            return `Yazar: ${getAuthorName(value)}`;
                        case 'author':
                            return `Yazar: ${getAuthorName(value)}`;
                        case 'title':
                            return `Başlık: "${value}"`;
                        case 'content':
                            return `İçerik: "${value}"`;
                        case 'keywords':
                            return `Anahtar Kelimeler: ${value}`;
                        default:
                            return `${key}: ${value}`;
                    }
                });
            
            if (activeFilters.length > 0) {
                return `Gelişmiş Arama Sonuçları`;
            } else {
                return 'Gelişmiş Arama - Tüm Sonuçlar';
            }
        }
        return `"${searchQuery}" için Arama Sonuçları`;
    };

    // Aktif filtreleri göster
    const getActiveFilters = () => {
        if (!isAdvancedSearch) return null;
        
        const activeFilters = Object.entries(advancedParams)
            .filter(([_, value]) => value && value.trim() !== '');
            
        if (activeFilters.length === 0) return null;
        
        // Yazar ismini ID'den bul
        const getAuthorName = (authorId) => {
            const writer = writers.find(w => w.id.toString() === authorId.toString());
            return writer ? `${writer.name} ${writer.surname}` : `Yazar ID: ${authorId}`;
        };
        
        return (
            <div className="active-filters-display mb-3">
                <strong>Arama Kriterleri:</strong>
                <div className="d-flex flex-wrap gap-2 mt-2">
                    {activeFilters.map(([key, value]) => {
                        let displayText = '';
                        switch(key) {
                            case 'selectedAuthor':
                                displayText = `Yazar: ${getAuthorName(value)}`;
                                break;
                            case 'author':
                                displayText = `Yazar: ${getAuthorName(value)}`;
                                break;
                            case 'title':
                                displayText = `Başlık: "${value}"`;
                                break;
                            case 'content':
                                displayText = `İçerik: "${value}"`;
                                break;
                            case 'keywords':
                                displayText = `Anahtar Kelimeler: ${value}`;
                                break;
                            default:
                                displayText = `${key}: ${value}`;
                        }
                        
                        return (
                            <span key={key} className="filter-badge">
                                {displayText}
                            </span>
                        );
                    })}
                </div>
            </div>
        );
    };

    // Tarih formatlama fonksiyonu
    const formatDate = (dateString) => {
        if (!dateString) return '';
        
        try {
            const date = new Date(dateString);
            if (isNaN(date.getTime())) return dateString; // Geçersiz tarih ise orijinalini döndür
            
            const day = date.getDate().toString().padStart(2, '0');
            const month = (date.getMonth() + 1).toString().padStart(2, '0');
            const year = date.getFullYear();
            
            return `${day}-${month}-${year}`;
        } catch (error) {
            console.error('Tarih formatlanırken hata:', error);
            return dateString;
        }
    };

    // Arama kelimelerini vurgulama fonksiyonu
    const highlightSearchTerms = (text, searchTerms) => {
        if (!text || !searchTerms || searchTerms.length === 0) return text;
        
        let highlightedText = text;
        
        searchTerms.forEach(term => {
            if (term && term.trim()) {
                // Global case-insensitive regex oluştur
                const regex = new RegExp(`(${term.trim().replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
                highlightedText = highlightedText.replace(regex, '<mark class="search-highlight">$1</mark>');
            }
        });
        
        return highlightedText;
    };

    // Arama parametrelerinden tüm arama terimlerini topla
    const getSearchTerms = () => {
        const terms = [];
        
        if (advancedParams.title) {
            terms.push(...advancedParams.title.split(' ').filter(t => t.length > 2));
        }
        if (advancedParams.content) {
            terms.push(...advancedParams.content.split(' ').filter(t => t.length > 2));
        }
        if (advancedParams.keywords) {
            terms.push(...advancedParams.keywords.split(',').map(k => k.trim()).filter(t => t.length > 2));
        }
        if (searchQuery) {
            terms.push(...searchQuery.split(' ').filter(t => t.length > 2));
        }
        
        // Tekrar eden terimleri kaldır ve küçült
        return [...new Set(terms.map(term => term.toLowerCase()))];
    };

    const searchTerms = getSearchTerms();

    if (loading) {
        return (
            <Container fluid className="search-results-container py-5">
                <Row>
                    <Col md={{ span: 8, offset: 2 }}>
                        <div className="text-center">Yükleniyor...</div>
                    </Col>
                </Row>
            </Container>
        );
    }

    if (error) {
        return (
            <Container fluid className="search-results-container py-5">
                <Row>
                    <Col md={{ span: 8, offset: 2 }}>
                        <div className="text-center text-danger">{error}</div>
                    </Col>
                </Row>
            </Container>
        );
    }

    return (
        <Container fluid className="search-results-container py-5">
            <Row>
                {/* Filtreler - Sol Sidebar */}
                {/* <Col md={3} className="filters-sidebar">
                    <div className="filters-section">
                        <h4 className="filters-title">Filtreler</h4>
                        
                        
                        {isAdvancedSearch && (
                            <div className="active-filters mb-4">
                                <h5>Aktif Filtreler</h5>
                                {Object.entries(advancedParams).map(([key, value]) => (
                                    value && (
                                        <div key={key} className="active-filter-badge">
                                            {key}: {value}
                                        </div>
                                    )
                                ))}
                            </div>
                        )}

                
                        <div className="filter-group">
                            <h5>Yıl</h5>
                            {filters.years.map(year => (
                                <Form.Check
                                    key={year}
                                    type="checkbox"
                                    label={year}
                                    checked={selectedFilters.years.includes(year)}
                                    onChange={() => handleFilterChange('years', year)}
                                    className="filter-checkbox"
                                />
                            ))}
                        </div>

                    
                        <div className="filter-group">
                            <h5>Yazarlar</h5>
                            {filters.authors.map(author => (
                                <Form.Check
                                    key={author}
                                    type="checkbox"
                                    label={author}
                                    checked={selectedFilters.authors.includes(author)}
                                    onChange={() => handleFilterChange('authors', author)}
                                    className="filter-checkbox"
                                />
                            ))}
                        </div>

            
                        <div className="filter-group">
                            <h5>Konular</h5>
                            {filters.topics.map(topic => (
                                <Form.Check
                                    key={topic}
                                    type="checkbox"
                                    label={topic}
                                    checked={selectedFilters.topics.includes(topic)}
                                    onChange={() => handleFilterChange('topics', topic)}
                                    className="filter-checkbox"
                                />
                            ))}
                        </div>
                    </div>
                </Col> */}

                {/* Arama Sonuçları - Ana İçerik */}
                <Col md={{ span: 8, offset: 2 }}>
                    <div className="results-header d-flex justify-content-between align-items-center mb-4">
                        <div>
                            <h2>{getSearchTitle()}</h2>
                            <p className="text-muted">{results.length} sonuç bulundu</p>
                        </div>
                        {/* <Form.Select 
                            value={sortBy}
                            onChange={(e) => setSortBy(e.target.value)}
                            className="sort-select"
                        >
                            <option value="relevance">Alaka Düzeyi</option>
                            <option value="date">Tarih</option>
                            <option value="title">Başlık</option>
                        </Form.Select> */}
                    </div>

                    {getActiveFilters()}

                    <div className="results-list">
                        {results.length === 0 ? (
                            <div className="no-results text-center py-5">
                                <a href="/gelismisArama" className="text-decoration-none text-red text-center">
                                <h4>Sonuç bulunamadı</h4>
                                <p>Arama kriterlerinizi değiştirerek tekrar denemek için tıklayınız.</p>
                                </a>
                            </div>
                        ) : (
                            results.map(result => (
                                <Link 
                                    to={`/article/${result.id}`}
                                    key={result.id}
                                    className="result-link"
                                >
                                    <Card className="result-card mb-4">
                                        <Card.Body>
                                            <div className="d-flex align-items-start justify-content-between">
                                                <div className="flex-grow-1">
                                                    <Card.Title 
                                                        className="result-title"
                                                        dangerouslySetInnerHTML={{
                                                            __html: highlightSearchTerms(result.title, searchTerms)
                                                        }}
                                                    />
                                                    
                                                    {result.definition && (
                                                        <Card.Subtitle 
                                                            className="mb-2 text-muted"
                                                            dangerouslySetInnerHTML={{
                                                                __html: highlightSearchTerms(result.definition, searchTerms)
                                                            }}
                                                        />
                                                    )}
                                                    
                                                    <div className="result-meta mb-3">
                                                        <div className="d-flex align-items-center mb-2">
                                                            {result.profileImageUrl && (
                                                                <img 
                                                                    src={result.profileImageUrl} 
                                                                    alt={result.writerName}
                                                                    className="author-avatar me-2"
                                                                    style={{
                                                                        width: '82px',
                                                                        height: '82px',
                                                                        borderRadius: '50%',
                                                                        objectFit: 'cover'
                                                                    }}
                                                                />
                                                            )}
                                                            <div>
                                                                <span 
                                                                    className="author fw-bold"
                                                                    dangerouslySetInnerHTML={{
                                                                        __html: highlightSearchTerms(result.writerName, searchTerms)
                                                                    }}
                                                                />
                                                                {/* {result.inst && (
                                                                    <div className="institution text-muted small">
                                                                        {result.inst}
                                                                    </div>
                                                                )} */}
                                                            </div>
                                                        </div>
                                                        
                                                        {/* {result.email && (
                                                            <div className="email text-muted small">
                                                                📧 {result.email}
                                                            </div>
                                                        )} */}
                                                    </div>

                                                    {result.abstract && (
                                                        <Card.Text 
                                                            className="result-abstract"
                                                            dangerouslySetInnerHTML={{
                                                                __html: highlightSearchTerms(result.abstract.length > 300 
                                                                    ? `${result.abstract.substring(0, 900)}...` 
                                                                    : result.abstract, searchTerms)
                                                            }}
                                                        />
                                                    )}

                                                    {result.keyword && (
                                                        <div className="result-keywords">
                                                            {result.keyword.split(',').map((keyword, index) => (
                                                                <span 
                                                                    key={index} 
                                                                    className="keyword-badge"
                                                                    dangerouslySetInnerHTML={{
                                                                        __html: highlightSearchTerms(keyword.trim(), searchTerms)
                                                                    }}
                                                                />
                                                            ))}
                                                        </div>
                                                    )}
                                                </div>
                                                
                                                <div className="result-actions">
                                                    <small className="text-muted">{formatDate(result.releaseDate)}</small>
                                                </div>
                                            </div>
                                        </Card.Body>
                                    </Card>
                                </Link>
                            ))
                        )}
                    </div>
                </Col>
            </Row>
        </Container>
    );
};

export default SearchResults; 
